import React, { Dispatch, useEffect, useState } from 'react';
import { SortDirection } from 'common/interfaces/IPageResponse';
import { SortIcons } from 'components/SortIcons/SortIcons';
import { Button, Spinner } from 'react-bootstrap';
import { SaleOrder, SaleOrderRow, SaleOrderSortFields, SaleOrderStatus } from 'models/sale-order';

import './SaleOrdersTable.scss';
import SaleOrderService from 'services/sale-order.service';
import moment from 'moment';
import { dateFormat } from 'common/dateFormatterConfig';

export function SaleOrdersTable({ orders, isRequesting, onOrderSelected, statusFilter, locationFilter }:
{ orders: SaleOrder[], isRequesting: boolean, onOrderSelected: any, statusFilter: any, locationFilter: string | null }) {

    const [sortedByField, setSortedByField]: [SaleOrderSortFields, Dispatch<any>] = useState(SaleOrderSortFields.ID);
    const [sortDirection, setSortDirection]: [SortDirection, Dispatch<any>] = useState<SortDirection>(SortDirection.DESC);
    const [sortedOrders, setSortedOrders]: [SaleOrder[], any] = useState([]);
    const [downloadingPdfForUid, setDownloadingPdfForUid] = useState<string | undefined>();

    const handleSortClick = (currentSortColumn: SaleOrderSortFields) => {
        if (sortedByField.match(currentSortColumn)) {
            setSortDirection(sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC);
        } else {
            setSortDirection(SortDirection.ASC);
            setSortedByField(currentSortColumn);
        }
    };

    useEffect(() => {
        setSortedOrders([...orders].sort((a: any, b: any) =>
            sortDirection === SortDirection.ASC ? 
            a[sortedByField].localeCompare(b[sortedByField]) :
            b[sortedByField].localeCompare(a[sortedByField])
        ))
    }, [sortDirection, sortedByField, orders]);

    const getPdf = (order: SaleOrder): void => {
        setDownloadingPdfForUid(order.uid);
        SaleOrderService.getSaleOrderFile(order.fileName!, order.docNumber || order.uid).then(() => setDownloadingPdfForUid(undefined));
    }

    const getStatusFromState = (state: string) => {
        if (state === SaleOrderStatus.ORDER_CREATED) {
            return <div className='sale-order-label sale-order-label--new-order'>Tellimus esitatud</div>;
        } else if (state === SaleOrderStatus.QUOTE_IN_PROGRESS) {
            return <div className='sale-order-label sale-order-label--awaiting-quote'>Pakkumuse ootel</div>;
        } else if (state === SaleOrderStatus.QUOTE_SUBMITTED) {
            return <div className='sale-order-label sale-order-label--awaiting-order-confirm'>Kinnitamise ootel</div>;
        } else if (state === SaleOrderStatus.CANCELLED) {
            return <div className='sale-order-label sale-order-label--cancelled'>Tühistatud</div>;
        } else if(state === SaleOrderStatus.ORDER_CONFIRMED) {
            return <div className='sale-order-label sale-order-label--in-progress'>Tellimus kinnitatud</div>;
        } else if(state === SaleOrderStatus.ORDER_WAITING_PAYMENT) {
            return <div className='sale-order-label sale-order-label--awaiting-order-confirm'>Ettemaks tasumata</div>;
        } else if(state === SaleOrderStatus.ORDER_IN_PROGRESS) {
            return <div className='sale-order-label sale-order-label--in-progress'>Tellimus töös</div>;
        } else if (state === SaleOrderStatus.ORDER_ISSUED) {
            return <div className='sale-order-label sale-order-label--invoice-created'>Tellimus väljastatud</div>;
        }
        return '-';
    }

    const tableRows = sortedOrders.filter(o => 
        (!statusFilter || o.docState === statusFilter.value) &&
        (!locationFilter || o.location.address === locationFilter)
    ).map((order: SaleOrder, index: number) =>
        <tbody key={'body-' + order.externalId + '-' + index} className="sale-order-table-body">
            { order.rows.map((row: SaleOrderRow, rowIndex: number) => (
                <tr key={'row-' + row.rowNo + '_' + index}>
                    <td>
                        <button className='btn btn-link p-0' onClick={() => onOrderSelected(order)}><strong>{rowIndex ? '' : order.uid}</strong></button>
                    </td>
                    <td>
                        { rowIndex ? '' : order.orderDate ? moment(order.orderDate).format(dateFormat) : '-' }
                    </td>
                    <td>
                        { rowIndex ? '' : order.desiredDeliveryDate ? moment(order.desiredDeliveryDate).format(dateFormat) : '-' }
                    </td>
                    <td>
                        {row.productName}
                    </td>
                    <td>
                        {row.quantity}L
                    </td>
                    <td>
                        {row.price ? row.price + '€/L' : '-'}
                    </td>
                    <td>
                        { rowIndex ? '' : order.location?.address ?? '-' }
                    </td>
                    <td>
                        {rowIndex ? '' : getStatusFromState(order.docState)}
                    </td>
                    <td>{rowIndex ? '' : ( order.validUntil && order.validUntil !== '0' ? order.validUntil + ' päeva' : 'Ettemaks' )}</td>
                    {false && !rowIndex && <td rowSpan={order.rows.length}>
                        {order.canChange && <button className='btn btn-primary btn-sm' onClick={() => onOrderSelected(order)}>Muuda</button>}
                    </td>}
                    <td className='d-flex align-items-center'>
                        { order.docNumber ? <span className='mr-2'>{ order.docNumber }</span> : null }
                        { order.fileName ? <Button size="sm" variant="secondary" className="d-flex align-items-center btn-outline-icon" onClick={() => order.fileName ? getPdf(order) : null} >
                            {
                                downloadingPdfForUid === order.uid ?
                                <Spinner size="sm" animation="border" /> :
                                <div className='icon__file action-icon icon--16'></div>
                            }  
                        </Button> : null}
                    </td>
                </tr>
            ))}
        </tbody>
    );
    return (
        <div className="table__container scroll-icon">
            <table className='sale-orders__table table table-responsive-md table-hover'>
            <thead>
            <tr>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center" onClick={() => handleSortClick(SaleOrderSortFields.ID)}>
                    <span>Nr</span>
                    <SortIcons
                        sortedByField={sortedByField}
                        sortDirection={sortDirection}
                        currentSortColumn={SaleOrderSortFields.ID}/>
                    </div>
                </th>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center" onClick={() => handleSortClick(SaleOrderSortFields.ORDER_DATE)}>
                    <span>Tellimuse esitamise KP</span>
                    <SortIcons
                        sortedByField={sortedByField}
                        sortDirection={sortDirection}
                        currentSortColumn={SaleOrderSortFields.ORDER_DATE}/>
                    </div>
                </th>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center" onClick={() => handleSortClick(SaleOrderSortFields.DELIVERY_DATE)}>
                    <span>Soovitud tarne KP</span>
                    <SortIcons
                        sortedByField={sortedByField}
                        sortDirection={sortDirection}
                        currentSortColumn={SaleOrderSortFields.DELIVERY_DATE}/>
                    </div>
                </th>                  
                <th>
                    Kaup
                </th>
               <th>
                    Kogus
                </th>
                <th>
                    Hind KM-ta
                </th>
                <th>
                    Mahalaadimiskoht
                </th>
                <th className="sort-down-up-hover mw-card-nr">
                    <div className="d-flex align-items-center" onClick={() => handleSortClick(SaleOrderSortFields.STATUS)}>
                    <span>Staatus</span>
                    <SortIcons
                        sortedByField={sortedByField}
                        sortDirection={sortDirection}
                        currentSortColumn={SaleOrderSortFields.STATUS}/>
                    </div>
                </th>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center" onClick={() => handleSortClick(SaleOrderSortFields.DATE)}>
                    <span>Maksetähtaeg</span>
                    <SortIcons
                        sortedByField={sortedByField}
                        sortDirection={sortDirection}
                        currentSortColumn={SaleOrderSortFields.DATE}/>
                    </div>
                </th>
                <th>Pakkumine</th>
            </tr>
            </thead>
            {isRequesting ?
            <tbody>
                <tr>
                    <td colSpan={9}>
                        <div className="p-4 w-100 d-flex align-items-center justify-content-center">
                            <Spinner animation="grow" />
                        </div>
                    </td>
                </tr>
            </tbody>:
            tableRows}
        </table>
        </div>
    )
}